import classNames from 'classnames';
import type { HTMLAttributes, ReactNode } from 'react';

import { Icon } from '../Icon';
import { Typography } from '../Typography';

import styles from './MenuItem.module.css';

export type MenuItemSize = 'sm' | 'md';

export interface MenuItemProps extends Omit<HTMLAttributes<HTMLDivElement>, 'onSelect'> {
  /** Primary label. */
  children: ReactNode;
  /** Muted secondary text shown inline after the label (e.g. `@handle`). */
  supportingText?: ReactNode;
  /**
   * Muted meta pushed to the row's end (e.g. `8 labs`) — unlike `supportingText`, which sits
   * right after the label.
   */
  trailing?: ReactNode;
  /**
   * A second line under the label (e.g. a location path). Turns the row into a two-line result:
   * the label drops to regular weight and the leading glyph aligns with the first line.
   */
  description?: ReactNode;
  /** Leading element — an icon, avatar, status dot, or checkbox. */
  leading?: ReactNode;
  /** Marks the row selected (shows a trailing check for single-select menus). */
  selected?: boolean;
  /** Keyboard-highlighted row (visually matches hover); driven by the menu. */
  active?: boolean;
  disabled?: boolean;
  size?: MenuItemSize;
  /**
   * Multi-select row: render a leading presentational checkbox (reflecting `selected`)
   * instead of the trailing check. Selection is still conveyed via `aria-selected`.
   */
  checkbox?: boolean;
  /** Invoked on click / Enter (not when disabled). */
  onSelect?: () => void;
  /** ARIA role — `option` inside a listbox (default) or `menuitem` inside a menu. */
  role?: 'option' | 'menuitem';
}

/**
 * A single row inside a `DropdownMenu`: leading slot + label + inline supporting text,
 * with a trailing check when selected. Hover/active/disabled states are visual only;
 * keyboard navigation is owned by the menu.
 *
 * @interim Seeded from the Figma "Dropdown menu item"; replaceable by the UI Kit.
 */
export function MenuItem({
  children,
  supportingText,
  trailing,
  description,
  leading,
  selected = false,
  active = false,
  disabled = false,
  size = 'md',
  checkbox = false,
  onSelect,
  role = 'option',
  className,
  ...rest
}: MenuItemProps) {
  return (
    // Keyboard navigation is owned by the parent listbox/menu (roving activedescendant),
    // so the option itself only needs a click handler for the mouse.
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events
    <div
      {...rest}
      role={role}
      // Menu items take roving focus (arrow keys); listbox options stay unfocused and
      // are tracked by the combobox via aria-activedescendant.
      tabIndex={role === 'menuitem' && !disabled ? -1 : undefined}
      aria-selected={role === 'option' ? selected : undefined}
      aria-disabled={disabled || undefined}
      className={classNames(styles.item, styles[size], className)}
      onClick={
        disabled
          ? undefined
          : () => {
              onSelect?.();
            }
      }
    >
      <div
        className={classNames(
          styles.content,
          active && styles.active,
          disabled && styles.disabled,
          description != null && styles.twoLine,
        )}
      >
        {checkbox && (
          <span className={classNames(styles.checkbox, selected && styles.checkboxOn)} aria-hidden>
            {selected && (
              <Icon name="check" size={14} className={classNames(styles.checkboxGlyph)} />
            )}
          </span>
        )}
        {leading != null && <span className={styles.leading}>{leading}</span>}
        <span className={styles.text}>
          <Typography
            as="span"
            variant="text-md"
            weight={description != null ? 'regular' : 'medium'}
            className={classNames(styles.label)}
          >
            {children}
          </Typography>
          {supportingText != null && (
            <Typography as="span" variant="text-md" className={classNames(styles.supporting)}>
              {supportingText}
            </Typography>
          )}
          {description != null && (
            <Typography as="span" variant="text-sm" className={classNames(styles.description)}>
              {description}
            </Typography>
          )}
        </span>
        {trailing != null && (
          <Typography as="span" variant="text-md" className={classNames(styles.trailing)}>
            {trailing}
          </Typography>
        )}
        {selected && !checkbox && (
          <Icon name="check" size={20} className={classNames(styles.check)} />
        )}
      </div>
    </div>
  );
}
